import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { TranslateService }   from '@ngx-translate/core';
import { UIRouter }           from '@uirouter/core';
import $                      from 'jquery';

import { BoxTogglerService }                   from '../core/services/box-toggler.service';
import { CustomerModel }                       from '../customers/models/customer';
import { MessagesService }                     from '../messages/messages.service';
import { Table }                               from '../table/types/table';
import { VehicleVisualModel }                  from './typings/vehicle-visuals';
import { VehicleVisualsActionsComponent }      from './vehicle-visuals.actions.component';
import { VehicleVisualsCatalogSelectionModel } from './typings/vehicle-visuals.selection';
import { VehicleVisualsLevel }                 from './typings/vehicle-visuals.level';
import { VehicleVisualsPreferencesModel }      from './typings/vehicle-visuals.preferences';
import { VehicleVisualsService }               from './vehicle-visuals.service';

import {
  VehicleVisualsDocumentModel,
  VehicleVisualsDocumentType
} from './typings/vehicle-visuals.document';

@Component({
  selector   : 'wor-vehicle-visuals-catalog',
  template: require('./vehicle-visuals.catalog.component.html')
})
export class VehicleVisualsCatalogComponent implements AfterViewInit, OnInit {
  @BlockUI('vehicleVisualsBoxBlock') block : NgBlockUI;

  @Input() customer : CustomerModel;
  @Input() record   : VehicleVisualsDocumentModel;
  @Input() type     : VehicleVisualsDocumentType = 'invoice';

  @ViewChild('content') content : ElementRef<HTMLElement>;
  @ViewChild('toggler') toggler : ElementRef<HTMLElement>;

  activeTab     = 1;
  catalog       : Array<VehicleVisualModel> = [];
  interactive   = false;
  isOpen        = false;
  labelField    = 'en_AU';
  loading       = true;
  query         = '';
  searched      = false;
  searchResults : Array<VehicleVisualModel> = [];

  selection : VehicleVisualsCatalogSelectionModel = this.vehicleVisualsService.emptySelection();

  tableSettings : Table = {
    ...this.vehicleVisualsService.searchResultsTableSettings([
      {
        component: {
          bindings: {
            outputs: {
              onAttachClick: ( visual : VehicleVisualModel ) => this.attach(visual),
              onEmailClick : ( visual : VehicleVisualModel ) => this.email(visual),
              onLaunchClick: ( visual : VehicleVisualModel ) => this.launchVisual(visual)
            }
          },
          enabled: true,
          factory: VehicleVisualsActionsComponent
        },
        sortable: false
      }
    ]),
    rows: {
      highlight: {
        enabled: true
      }
    },
    static: true
  };

  constructor (
    private boxTogglerService    : BoxTogglerService,
    private messagesService      : MessagesService,
    private router               : UIRouter,
    private translateService     : TranslateService,
    private vehicleVisualsService: VehicleVisualsService
  ) {}

  ngOnInit () : void {
    this.vehicleVisualsService.getUserPreferences()
    .then(( preferences : VehicleVisualsPreferencesModel ) => {
      this.labelField = preferences?.default_lang || this.labelField;
    })
    .catch(error => this.messagesService.error(error));

    this.getCatalog();
  }

  ngAfterViewInit () : void {
    this.boxTogglerService.close($(this.toggler.nativeElement), $(this.content.nativeElement), true);
  }

  attach ( visual : VehicleVisualModel ) : void {
    const attached = this.type === 'invoice'
      ? this.vehicleVisualsService.attachToInvoice(this.record, visual, this.interactive)
        .then(() => this.translateService.instant('JS_SPACE.MESSAGES.ATTACHED_TO_INVOICE'))
      : this.vehicleVisualsService.attachToInspection(this.record, visual, this.interactive)
        .then(() => this.translateService.instant('JS_SPACE.MESSAGES.ATTACHED_TO_INSPECTION'));

    attached
    .then(( message : string ) => {
      this.messagesService.show(message, 'success');

      return this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }

  email ( visual : VehicleVisualModel ) : void {
    this.vehicleVisualsService.email(this.customer, visual)
    .then(( sent : boolean ) => {
      if ( sent ) {
        this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.EMAIL_SENT'), 'success');
      }
    })
    .catch(error => this.messagesService.error(error));
  }

  getCatalog () : Promise<void> {
    return this.vehicleVisualsService.getCatalog(this.interactive)
    .then(( catalog : Array<VehicleVisualModel> ) => {
      this.catalog = catalog;
    })
    .catch(error => this.messagesService.error(error))
    .finally(() => {
      this.loading = false;
    });
  }

  hasChildren ( visual : VehicleVisualModel | null ) : boolean {
    return !!visual?.children?.length;
  }

  isSelected ( visual : VehicleVisualModel, level : VehicleVisualsLevel ) : boolean {
    return this.selection[level]?.part_id === visual.part_id;
  }

  launchVisual ( visual : VehicleVisualModel ) : void {
    this.vehicleVisualsService.launch(visual, this.interactive)
    .catch(error => this.messagesService.error(error));
  }

  refreshCatalog () : void {
    this.block.start();

    this.getCatalog()
    .finally(() => this.block.stop());
  }

  search () : void {
    this.searchResults = [];

    this.vehicleVisualsService.search(this.query)
    .then(( results : Array<VehicleVisualModel> ) => {
      this.searchResults = results;
      this.searched      = true;
    })
    .catch(error => this.messagesService.error(error));
  }

  select ( level : VehicleVisualsLevel, visual : VehicleVisualModel ) : void {
    this.selection = this.vehicleVisualsService.selectLevel(this.selection, level, this.isSelected(visual, level)
      ? null
      : visual);
  }

  selectTab ( event : Event, tab : number ) : void {
    event.preventDefault();

    this.activeTab = tab;
  }

  toggle ( event : Event ) : void {
    event.preventDefault();

    const content = $(this.content.nativeElement);
    const toggler = $(this.toggler.nativeElement);

    this.isOpen = !this.isOpen;

    if ( this.isOpen ) {
      this.boxTogglerService.open(toggler, content);
    }
    else {
      this.boxTogglerService.close(toggler, content);
    }
  }
}
